import { baseApi, API_BASE_URL } from "./baseApi";

/** Converts a relative API photo URL to a full URL with the API host. */
export const getFullEmployeePhotoUrl = (relativeUrl: string | null | undefined): string | null => {
  if (!relativeUrl) return null;
  if (relativeUrl.startsWith("http")) return relativeUrl;

  // The backend sometimes stores the absolute server filesystem path
  // (e.g. "h:\root\home\...\www\dialin24api\Storage\employees\profile\1.jpg")
  // instead of a public URL. Only the part from "Storage" onward is web-servable,
  // so strip everything before it and normalize backslashes to forward slashes.
  const normalized = relativeUrl.replace(/\\/g, "/");
  const storageIndex = normalized.toLowerCase().indexOf("/storage/");
  const relativePath =
    storageIndex >= 0 ? normalized.slice(storageIndex) : normalized;

  // Join host + path with exactly one slash, regardless of how many
  // leading/trailing slashes either side already has (the backend
  // sometimes returns paths with a doubled leading slash).
  const host = API_BASE_URL.replace(/\/api$/, "").replace(/\/+$/, "");
  const path = relativePath.replace(/^\/+/, "");
  const fullUrl = `${host}/${path}`
  .replace(/\\/g, "/")        // Replace backslashes with forward slashes
  .replace(/([^:]\/)\/+/g, "$1"); // Remove duplicate slashes except after protocol
  return fullUrl;
};

export interface EmployeeApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

export interface EmployeePayload {
  firstName: string;
  lastName: string;
  gender?: string | null;
  maritalStatus?: string | null;
  email: string;
  phone: string;
  department: string;
  role: string;
  status?: string;
  joinedAt?: string | null;
  aadhaarNumber?: string | null;
}

export interface EmployeeRecord extends EmployeePayload {
  id: number;
  createdAt?: string;
  updatedAt?: string | null;
  aadhaarNumber?: string | null;
  profilePhotoUrl?: string | null;
  aadhaarPhotoUrl?: string | null;
}

const normalizeEmployee = (item: Record<string, any>): EmployeeRecord => ({
  id: Number(item.id ?? 0),
  firstName: String(item.firstName ?? ""),
  lastName: String(item.lastName ?? ""),
  gender: item.gender ?? null,
  maritalStatus: item.maritalStatus ?? null,
  email: String(item.email ?? ""),
  phone: String(item.phone ?? ""),
  department: String(item.department ?? ""),
  role: String(item.role ?? ""),
  status: String(item.status ?? "active"),
  joinedAt: item.joinedAt ?? null,
  createdAt: item.createdAt ?? undefined,
  updatedAt: item.updatedAt ?? null,
  aadhaarNumber: item.aadhaarNumber ?? null,
  profilePhotoUrl: item.profilePhotoUrl ?? null,
  aadhaarPhotoUrl: item.aadhaarPhotoUrl ?? null,
});

const getItems = (response: EmployeeApiResponse<unknown> | unknown) => {
  const data = (response as EmployeeApiResponse<unknown>)?.data ?? response;
  if (Array.isArray(data)) return data as Record<string, any>[];
  return [] as Record<string, any>[];
};

export const employeesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchEmployees: builder.query<EmployeeRecord[], string | void>({
      query: (search) => ({
        url: search ? `/Employee?search=${encodeURIComponent(search)}` : "/Employee",
        method: "GET",
      }),
      transformResponse: (response: EmployeeApiResponse<unknown> | unknown) =>
        getItems(response).map(normalizeEmployee),
    }),

    fetchEmployeeById: builder.query<EmployeeRecord, number>({
      query: (id) => ({ url: `/Employee/${id}`, method: "GET" }),
      transformResponse: (response: EmployeeApiResponse<unknown> | unknown) => {
        const data = (response as EmployeeApiResponse<unknown>)?.data ?? response;
        return normalizeEmployee(data as Record<string, any>);
      },
    }),
    createEmployee: builder.mutation<EmployeeApiResponse, EmployeePayload>({
      query: (payload) => ({
        url: "/Employee",
        method: "POST",
        body: payload,
      }),
    }),
    updateEmployee: builder.mutation<
      EmployeeApiResponse,
      { id: number; payload: EmployeePayload }
    >({
      query: ({ id, payload }) => ({
        url: `/Employee/${id}`,
        method: "PUT",
        body: { id, ...payload },
      }),
    }),
    deleteEmployee: builder.mutation<EmployeeApiResponse, number>({
      query: (id) => ({
        url: `/Employee/${id}`,
        method: "DELETE",
      }),
    }),

    uploadEmployeePhotos: builder.mutation<
      EmployeeApiResponse<EmployeeRecord>,
      { id: number; profilePhoto?: File | null; aadhaarPhoto?: File | null }
    >({
      query: ({ id, profilePhoto, aadhaarPhoto }) => {
        const form = new FormData();
        if (profilePhoto) form.append("profilePhoto", profilePhoto);
        if (aadhaarPhoto) form.append("aadhaarPhoto", aadhaarPhoto);
        return { url: `/Employee/${id}/photos`, method: "POST", body: form };
      },
      transformResponse: (response: EmployeeApiResponse<unknown> | unknown) => {
        const r = response as EmployeeApiResponse<unknown>;
        const data = r?.data ?? response;
        return { ...r, data: normalizeEmployee(data as Record<string, any>) };
      },
    }),
  }),
});

export const {
  useFetchEmployeesQuery,
  useFetchEmployeeByIdQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useUploadEmployeePhotosMutation,
} = employeesApi;
